import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, agentsAPI } from '../../services/api';
import { toast } from 'react-toastify';

const AssignAgentsToSupervisor = () => {
	const { supervisorId } = useParams();
	const navigate = useNavigate();
	const [supervisor, setSupervisor] = useState(null);
	const [allAgents, setAllAgents] = useState([]);
	const [assignedIds, setAssignedIds] = useState([]); // already assigned (from server)
	const [toAssignIds, setToAssignIds] = useState([]); // selected in this page to add
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);

	useEffect(() => {
		if (!supervisorId) return;
		const fetchData = async () => {
			try {
				setLoading(true);
				const [supervisorsData, agentsData, assignedData] = await Promise.all([
					adminAPI.getSupervisors(),
					agentsAPI.getAll(),
					adminAPI.getSupervisorAgents(supervisorId),
				]);
				const list = Array.isArray(supervisorsData) ? supervisorsData : [];
				setSupervisor(list.find((s) => s.id === supervisorId) || null);
				setAllAgents(Array.isArray(agentsData) ? agentsData : []);
				const ids = (Array.isArray(assignedData) ? assignedData : []).map((a) => a.id);
				setAssignedIds(ids);
				setToAssignIds([]);
			} catch (err) {
				toast.error('Failed to load data');
				setAllAgents([]);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [supervisorId]);

	const toggle = (id, checked) => {
		if (checked) setToAssignIds((prev) => [...prev, id]);
		else setToAssignIds((prev) => prev.filter((x) => x !== id));
	};

	const handleConfirm = async () => {
		try {
			setSaving(true);
			await adminAPI.assignAgentsToSupervisor(supervisorId, [...assignedIds, ...toAssignIds]);
			toast.success('Agents updated for this supervisor');
			setShowConfirm(false);
			setTimeout(() => navigate('/admin/supervisor-management'), 1500);
		} catch (err) {
			toast.error(err.response?.data?.message || 'Failed to assign agents');
			setSaving(false);
		}
	};

	const displayName = (a) => {
		const u = a.user || a;
		return [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || '—';
	};

	if (loading && !supervisor) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Loading...</p>
			</div>
		);
	}

	const availableAgents = allAgents.filter((a) => !assignedIds.includes(a.id));

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-[#001E3C] border-b border-white/10'>
				<div className='flex items-center gap-4 mb-4'>
					<button type='button' onClick={() => navigate('/admin/supervisor-management')} className='glass-card p-3 rounded-xl'>
						<ChevronLeft className='w-5 h-5 text-white' />
					</button>
					<div>
						<h2 className='text-xl font-black uppercase text-white'>Assign agents</h2>
						<p className='text-sm text-white/60'>{supervisor ? [supervisor.firstName, supervisor.lastName].filter(Boolean).join(' ') || supervisor.email : ''}</p>
					</div>
				</div>
			</header>
			<main className='flex-1 overflow-y-auto p-6 no-scrollbar'>
				<p className='text-sm text-white/60 mb-4'>Select agents to add. Already assigned agents are not shown. Save to apply.</p>
				<section>
					<h3 className='text-sm font-bold text-white/80 mb-3'>Available agents ({availableAgents.length})</h3>
					<div className='space-y-3'>
						{availableAgents.map((agent) => {
							const id = agent.id;
							const checked = toAssignIds.includes(id);
							return (
								<div key={id} className='glass-card p-4 flex items-center gap-4'>
									<input
										type='checkbox'
										className='w-5 h-5'
										checked={checked}
										onChange={(e) => toggle(id, e.target.checked)}
									/>
									<div className='flex-1'>
										<p className='font-bold text-white'>{displayName(agent)}</p>
										<p className='text-sm text-white/60'>{agent.user?.email || agent.email}</p>
									</div>
									{checked && <span className='text-xs text-[#00E676] font-bold'>To add</span>}
								</div>
							);
						})}
					</div>
				</section>
				{availableAgents.length === 0 && !loading && <p className='text-white/60'>No agents available to assign. All agents are already assigned to this supervisor.</p>}
			</main>
			<footer className='p-6 border-t border-white/10'>
				<button type='button' onClick={() => setShowConfirm(true)} disabled={loading} className='w-full py-4 rounded-2xl btn-gold font-black uppercase text-sm' style={{ opacity: loading ? 0.5 : 1 }}>
					Save assignment
				</button>
			</footer>
			{showConfirm && (
				<div className='fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4'>
					<div className='bg-[#001E3C] rounded-2xl p-6 w-full max-w-md border border-white/10'>
						<h3 className='text-xl font-black text-white mb-4'>Confirm</h3>
						<p className='text-white/80 mb-6'>Add {toAssignIds.length} agent(s) to this supervisor? (They will join the {assignedIds.length} already assigned.)</p>
						<div className='flex gap-4'>
							<button type='button' onClick={() => setShowConfirm(false)} className='flex-1 py-3 rounded-xl glass-card text-white font-bold'>Cancel</button>
							<button type='button' onClick={handleConfirm} disabled={saving} className='flex-1 py-3 rounded-xl btn-gold font-black uppercase'>{saving ? 'Saving...' : 'Confirm'}</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AssignAgentsToSupervisor;
